import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../../../NewInventory/Icon";
import useDebounce from "../../../../hooks/useDebouncs";
import "../../searchFilterBar.css";
import "../../poDropdownOption.css";

const VENDOR_SCROLL_THRESHOLD_PX = 48;
const VENDOR_SEARCH_DEBOUNCE_MS = 300;

const optionRow = {
  width: "100%",
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  gap: 6,
  minHeight: 32,
  padding: "4px 10px",
  borderRadius: 6,
  border: "none",
  cursor: "pointer",
  fontSize: 12.5,
  color: "var(--qv-tx-0f1115, #0f1115)",
  fontFamily: "CircularMedium",
};

const SupplierSelectDropdown = ({
  vendors = [],
  vendorId,
  selectedVendor = null,
  onPick,
  disabled = false,
  error = "",
  onSearchChange,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  loading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const serverSearch = Boolean(onSearchChange);
  const debouncedQuery = useDebounce(query, VENDOR_SEARCH_DEBOUNCE_MS);
  const resolvedVendorId = vendorId != null ? String(vendorId).trim() : "";
  const vendor =
    (resolvedVendorId
      ? (selectedVendor &&
          String(selectedVendor.id) === resolvedVendorId &&
          selectedVendor) ||
        vendors.find((v) => String(v.id) === resolvedVendorId)
      : null) ||
    (selectedVendor?.name ? selectedVendor : null);

  const isNoVendorSelected = !resolvedVendorId && !selectedVendor?.name;
  const hasVendor = Boolean(vendor?.name);

  const isVendorRowActive = (row) => {
    if (resolvedVendorId && String(row.id) === resolvedVendorId) return true;
    if (!resolvedVendorId && selectedVendor?.name) {
      return (
        String(row.name || "")
          .trim()
          .toLowerCase() ===
        String(selectedVendor.name || "")
          .trim()
          .toLowerCase()
      );
    }
    return false;
  };

  useEffect(() => {
    if (!serverSearch) return;
    onSearchChange(debouncedQuery);
  }, [debouncedQuery, onSearchChange, serverSearch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        if (serverSearch) {
          setQuery("");
          onSearchChange("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onSearchChange, serverSearch]);

  const filtered = useMemo(() => {
    if (serverSearch) return vendors;
    if (!query) return vendors;
    const q = query.toLowerCase();
    return vendors.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.contact || "").toLowerCase().includes(q) ||
        (v.terms || "").toLowerCase().includes(q)
    );
  }, [vendors, query, serverSearch]);

  const isSearchDebouncing =
    serverSearch && query.trim() !== debouncedQuery.trim();
  const showListLoading =
    (query.trim() && (isSearchDebouncing || (loading && !loadingMore))) ||
    (!query.trim() && loading && !loadingMore && filtered.length === 0);

  const handleListScroll = (e) => {
    if (!onLoadMore || !hasMore || loadingMore) return;
    const el = e.currentTarget;
    if (
      el.scrollTop + el.clientHeight >=
      el.scrollHeight - VENDOR_SCROLL_THRESHOLD_PX
    ) {
      onLoadMore();
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => {
      if (prev && serverSearch) {
        setQuery("");
        onSearchChange("");
      }
      return !prev;
    });
  };

  const pick = (id) => {
    onPick(id);
    setOpen(false);
    setQuery("");
    if (serverSearch) onSearchChange("");
  };

  return (
    <div
      ref={ref}
      className="po-picker-filter"
      style={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        zIndex: open ? 120 : "auto",
      }}
    >
      <button
        type="button"
        className={`po-picker-filter-trigger${hasVendor ? "" : " is-empty"}${error ? " is-error" : ""}`}
        disabled={disabled}
        onClick={handleToggle}
        style={{
          height: 36,
          padding: "0 12px",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 8,
          overflow: "hidden",
          cursor: disabled ? "not-allowed" : "pointer",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "CircularMedium",
        }}
      >
        <Icon name="vendors" size={13} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {vendor?.name || "No Vendor"}
        </span>
        {vendor?.preferred ? (
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 99,
              background: "rgba(232,89,12,0.12)",
              color: "var(--qv-tx-a14a00, #a14a00)",
              fontWeight: 500,
              fontFamily: "CircularMedium",
              letterSpacing: 0.3,
              flexShrink: 0,
            }}
          >
            PREFERRED
          </span>
        ) : null}
        <Icon name="chevron-down" size={10} />
      </button>
      {open ? (
        <div
          className="po-vendor-picker-menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            width: "100%",
            background: "var(--qv-bg-ffffff, #fff)",
            border: "1px solid var(--qv-bd-e3e6eb, #e3e6eb)",
            borderRadius: 10,
            boxShadow: "0 18px 40px var(--qv-shadow-a15x17x21x120, rgba(15,17,21,0.12))",
            zIndex: 121,
            overflow: "hidden",
            maxHeight: 360,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <div style={{ padding: 8, borderBottom: "1px solid var(--qv-bd-eef0f3, #eef0f3)", flexShrink: 0 }}>
            <div className="po-vendor-search">
              <Icon name="search" size={13} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search vendors"
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontSize: 12.5,
                  color: "var(--qv-tx-0f1115, #0f1115)",
                  fontFamily: "inherit",
                }}
              />
              {query ? (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: "var(--qv-tx-7a818f, #7a818f)",
                    display: "flex",
                    alignItems: "center",
                    padding: 0,
                  }}
                >
                  <Icon name="close" size={11} />
                </button>
              ) : null}
            </div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 4 }} onScroll={handleListScroll}>
            <button
              type="button"
              onClick={() => pick("")}
              className={`po-dropdown-option${isNoVendorSelected ? " po-dropdown-option--active" : ""}`}
              style={optionRow}
            >
              <span style={{ flex: 1 }}>No Vendor</span>
              {isNoVendorSelected ? (
                <Icon name="check" size={13} color="var(--qv-brand-text)" />
              ) : null}
            </button>
            <div style={{ height: 1, background: "var(--qv-bg-eef0f3, #eef0f3)", margin: "4px 0" }} />
            {showListLoading ? (
              <div
                style={{
                  padding: "16px 10px",
                  textAlign: "center",
                  fontSize: 12.5,
                  color: "var(--qv-tx-7a818f, #7a818f)",
                }}
              >
                Loading vendors…
              </div>
            ) : filtered.length === 0 ? (
              query ? (
                <div
                  style={{
                    padding: "16px 10px",
                    textAlign: "center",
                    fontSize: 12.5,
                    color: "var(--qv-tx-7a818f, #7a818f)",
                  }}
                >
                  No Vendors match &quot;{query}&quot;.
                </div>
              ) : null
            ) : (
              filtered.map((v) => {
                const active = isVendorRowActive(v);
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => pick(v.id)}
                    className={`po-dropdown-option${active ? " po-dropdown-option--active" : ""}`}
                    style={optionRow}
                  >
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v.name}
                    </span>
                    {v.preferred ? (
                      <span
                        style={{
                          fontSize: 10,
                          padding: "2px 6px",
                          borderRadius: 99,
                          background: "rgba(232,89,12,0.12)",
                          color: "var(--qv-tx-a14a00, #a14a00)",
                          fontWeight: 500,
                          fontFamily: "CircularMedium",
                          letterSpacing: 0.3,
                          flexShrink: 0,
                        }}
                      >
                        PREFERRED
                      </span>
                    ) : null}
                    {active ? (
                      <Icon name="check" size={13} color="var(--qv-brand-text)" />
                    ) : null}
                  </button>
                );
              })
            )}
            {loadingMore ? (
              <div
                style={{
                  padding: "10px",
                  textAlign: "center",
                  fontSize: 12,
                  color: "var(--qv-tx-7a818f, #7a818f)",
                }}
              >
                Loading more…
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {error ? (
        <p
          style={{
            margin: "6px 0 0",
            fontSize: 12,
            color: "var(--qv-tx-c0392b, #c0392b)",
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default SupplierSelectDropdown;
